'use client';

import { SearchEngine, buildResultsPerPage } from '@coveo/atomic-react';
import { DEFAULT_SEARCH_PAGE_SIZE } from '@lib/constants/coveo';
import { useFlags } from '@lib/flags/amplitude/client';
import { Flags } from '@lib/flags/flagConstants';
import { useEffect, useMemo, useState } from 'react';

const RESULTS_PER_PAGE_LOCAL_STORAGE_KEY = 'as-results-per-page';

const getResultsPerPageFromLocalStorage = () => {
  const resultsPerPage = localStorage?.getItem(
    RESULTS_PER_PAGE_LOCAL_STORAGE_KEY
  );
  return resultsPerPage ? parseInt(resultsPerPage, 10) : undefined;
};

const setResultsPerPageToLocalStorage = (numberOfResults: number) => {
  localStorage?.setItem(
    RESULTS_PER_PAGE_LOCAL_STORAGE_KEY,
    numberOfResults.toString()
  );
};

export const useResultsPerPage = (searchEngine: SearchEngine | undefined) => {
  const { flags } = useFlags();
  const flag = flags[Flags.INCREASE_DEFAULT_RESULTS_PER_PAGE];
  const defaultPageSize = flag?.payload?.pageSize || DEFAULT_SEARCH_PAGE_SIZE;

  const initialNumberOfResults =
    getResultsPerPageFromLocalStorage() ?? defaultPageSize;
  const resultsPerPageController = useMemo(() => {
    return buildResultsPerPage(searchEngine!, {
      initialState: { numberOfResults: initialNumberOfResults },
    });
    // very important that initialNumberOfResults is in the dependency array
    // otherwise the controller will be recreated on every render
  }, [searchEngine, initialNumberOfResults]);
  const [resultsPerPageState, setResultsPerPageState] = useState(
    resultsPerPageController?.state
  );

  useEffect(
    () =>
      resultsPerPageController?.subscribe(() => {
        setResultsPerPageState(resultsPerPageController.state);
      }),
    [resultsPerPageController]
  );

  return {
    resultsPerPage: resultsPerPageState?.numberOfResults,
    setResultsPerPage: (numberOfResults: number) => {
      resultsPerPageController?.set(numberOfResults);
      setResultsPerPageToLocalStorage(numberOfResults);
    },
  };
};
